<template>
	<v-dialog
		v-model="dialog"
		persistent
		fullscreen
		transition="slide-x-reverse-transition"
	>
		<v-card flat class="item-picker-dialog d-flex flex-column">
			<div class="d-flex align-center px-2 py-2 flex-grow-0">
				<v-btn icon @click="dialog = false">
					<v-icon>{{ backIcon }}</v-icon>
				</v-btn>
				<span class="text-subtitle-1 font-weight-bold ms-1">{{ title }}</span>
			</div>
			<v-divider />

			<div class="pa-3 flex-grow-0">
				<v-text-field
					v-model="search"
					:placeholder="$T('search')"
					outlined
					dense
					clearable
					autofocus
					hide-details
					prepend-inner-icon="mdi-magnify"
				/>
			</div>

			<v-progress-linear v-if="loading || loadingPage" indeterminate rounded height="4" color="primary" class="flex-grow-0" />

			<v-card-text class="flex-grow-1 overflow-y-auto pa-0" @scroll="onScroll">
				<v-list v-if="filteredItems.length">
					<v-list-item-group :value="selectedValue" color="primary">
						<v-list-item
							v-for="item in filteredItems"
							:key="item[itemValue]"
							:value="item[itemValue]"
							@click="selectItem(item)"
						>
							<v-list-item-content>
								<v-list-item-title class="font-weight-medium">{{ item[itemText] }}</v-list-item-title>
							</v-list-item-content>
						</v-list-item>
					</v-list-item-group>
				</v-list>

				<div v-else-if="!loadingPage" class="d-flex flex-column justify-center align-center" style="height: 45vh">
					<v-icon x-large class="mb-3">mdi-text-search</v-icon>
					<span class="grey--text text--darken-2">{{ $T('no-search-results') }}</span>
				</div>
			</v-card-text>
		</v-card>
	</v-dialog>
</template>

<script>
import { toWesternDigits } from '@/helpers/functions'

const PAGE_SIZE = 30
const SEARCH_DEBOUNCE_MS = 350

export default {
	name: 'ItemPickerDialog',

	props: {
		title: {
			type: String,
			required: true
		},
		itemText: {
			type: String,
			default: 'name'
		},
		itemValue: {
			type: String,
			default: 'id'
		},
		loading: {
			type: Boolean,
			default: false
		}
	},

	data() {
		return {
			dialog: false,
			items: [],
			selectedValue: null,
			search: null,

			// server-paginated mode (large lists searched on the backend instead of held in memory - see openPaginated)
			fetchPage: null,
			limit: 0,
			hasMore: false,
			loadingPage: false,
			searchDebounceTimer: null
		}
	},

	watch: {
		search() {
			if (!this.fetchPage) return

			clearTimeout(this.searchDebounceTimer)
			this.searchDebounceTimer = setTimeout(() => {
				this.limit = PAGE_SIZE
				this.runQuery()
			}, SEARCH_DEBOUNCE_MS)
		}
	},

	computed: {
		backIcon() {
			return this.$vuetify.rtl ? 'mdi-arrow-right' : 'mdi-arrow-left'
		},

		filteredItems() {
			if (this.fetchPage) return this.items
			if (!this.search) return this.items

			const query = toWesternDigits(this.search).toLowerCase()

			return this.items.filter(item => toWesternDigits(item[this.itemText]).toLowerCase().includes(query))
		}
	},

	methods: {
		selectItem(item) {
			this.$emit('select', item[this.itemValue], item)
			this.dialog = false
		},

		runQuery() {
			this.loadingPage = true

			return this.fetchPage(this.search, this.limit)
				.then(items => {
					this.items = items
					this.hasMore = items.length === this.limit
				})
				.finally(() => {
					this.loadingPage = false
				})
		},

		onScroll(event) {
			if (!this.fetchPage || this.loadingPage || !this.hasMore) return

			const el = event.target
			if (el.scrollTop + el.clientHeight >= el.scrollHeight - 100) {
				this.limit += PAGE_SIZE
				this.runQuery()
			}
		},

		// static mode: items are already fully loaded by the parent and filtered here client-side - use
		// this for small, finite lists (e.g. regions, customer types)
		open(items, selectedValue = null) {
			this.fetchPage = null
			this.items = items
			this.selectedValue = selectedValue
			this.search = null
			this.dialog = true
		},

		// server-paginated mode: for large lists (e.g. ~11k customers) that can't be held in memory -
		// fetchPage(query, limit) resolves the current page of matching items from the backend
		openPaginated(fetchPage, selectedValue = null) {
			this.fetchPage = fetchPage
			this.selectedValue = selectedValue
			this.search = null
			this.limit = PAGE_SIZE
			this.items = []
			this.hasMore = false
			this.dialog = true
			this.runQuery()
		}
	}
}
</script>

<style scoped>
.item-picker-dialog {
	height: 100%;
}

.item-picker-dialog .v-card__text.overflow-y-auto {
	min-height: 0;
}
</style>
